/** Parses a Geotab .NET TimeSpan string ("d.hh:mm:ss" or "hh:mm:ss") or raw seconds number to hours. */
export function parseTimespanToHrs(val: string | number | null | undefined): number {
  if (val === null || val === undefined) return 0;
  if (typeof val === 'number') return Math.max(0, val) / 3600;
  const parts = val.split(':');
  if (parts.length === 3) {
    const dh   = parts[0].split('.');
    const days  = dh.length > 1 ? parseInt(dh[0]) : 0;
    const hours = parseInt(dh[dh.length - 1]) || 0;
    const mins  = parseInt(parts[1]) || 0;
    const secs  = parseFloat(parts[2]) || 0;
    return Math.max(0, days * 24 + hours + mins / 60 + secs / 3600);
  }
  const n = parseFloat(val);
  return isNaN(n) ? 0 : Math.max(0, n) / 3600;
}

// Excel's native "elapsed time" duration format. The square-bracketed [h] (vs. plain h) tells
// Excel to keep counting hours past 24 instead of wrapping into days, so e.g. 148.62 hours
// renders as "148:37:12" rather than silently losing the day count.
export const EXCEL_DURATION_FMT = '[h]:mm:ss';

// Excel time/date cells are serial fractions of a day (1 = 24 hours), so a decimal-hours value
// has to be divided by 24 before being paired with EXCEL_DURATION_FMT for the cell to render
// (and remain sortable/summable as a real number, not a formatted string) correctly.
export function excelDurationValue(hours: number): number {
  return hours / 24;
}

/** Formats decimal hours as "HH:MM:SS" (hours unpadded past 2 digits, e.g. "148:37:12"), matching
 *  the [h]:mm:ss convention used in the downloadable Excel reports so on-screen durations read
 *  the same way as the exported ones. */
export function formatHms(hours: number): string {
  const totalSeconds = Math.max(0, Math.round(hours * 3600));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export function formatTimeDiff(date: Date): string {
  const diff    = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours   = Math.floor(minutes / 60);
  const days    = Math.floor(hours / 24);
  if (days > 0)    return `${days}d ago`;
  if (hours > 0)   return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}
